import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { 
  FileText,
  Plus,
  Eye,
  X,
  CheckCircle,
  Clock,
  Printer,
  Warning,
  Shield,
  PencilSimple,
  User,
  Briefcase,
  FirstAidKit,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function MedicareFormsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('abn'); // 'abn' or 'mspq'
  const [loading, setLoading] = useState(false);
  
  // Patients list (for staff)
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  
  // ABN State
  const [abnForms, setAbnForms] = useState([]);
  const [showAbnForm, setShowAbnForm] = useState(false);
  const [abnData, setAbnData] = useState({
    patient_name: '',
    patient_medicare_id: '',
    items_services: [{ description: '', reason: '', estimated_cost: '' }]
  });
  
  // MSPQ State
  const [mspqForms, setMspqForms] = useState([]);
  const [showMspqForm, setShowMspqForm] = useState(false);
  const [currentMspq, setCurrentMspq] = useState(null);
  const [mspqData, setMspqData] = useState({
    patient_name: '',
    patient_dob: '',
    patient_medicare_id: '',
    is_employed: false,
    employer_name: '',
    employer_address: '',
    employment_status: '',
    spouse_employed: false,
    spouse_employer_name: '',
    spouse_employer_address: '',
    has_ghp: false,
    ghp_name: '',
    ghp_policy_number: '',
    ghp_through: '',
    has_other_insurance: false,
    other_insurance_type: '',
    other_insurance_name: '',
    other_insurance_policy: '',
    accident_date: '',
    has_esrd: false,
    esrd_start_date: '',
    has_black_lung: false,
    va_eligible: false,
    service_related: false,
    patient_signature: ''
  });
  
  // PDF Preview
  const [pdfHtml, setPdfHtml] = useState(null);
  
  // ABN Signing
  const [signingAbn, setSigningAbn] = useState(null);
  const [abnSignature, setAbnSignature] = useState('');
  const [abnChoice, setAbnChoice] = useState('');

  const isStaff = ['cma', 'provider', 'admin'].includes(user?.role);

  const loadPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data.patients || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading patients:', error);
    }
  };

  const loadForms = useCallback(async () => {
    if (!selectedPatient && isStaff) return;
    
    setLoading(true);
    try {
      const patientId = isStaff ? selectedPatient?.id : user?.id;
      if (!patientId) return;
      
      const [abnRes, mspqRes] = await Promise.all([
        api.get(`/medicare/abn/patient/${patientId}`),
        api.get(`/medicare/mspq/patient/${patientId}`)
      ]);
      
      setAbnForms(abnRes.data.abns || []);
      setMspqForms(mspqRes.data.mspqs || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading forms:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPatient, isStaff, user?.id]);

  useEffect(() => {
    if (isStaff) {
      loadPatients();
    } else {
      // Patient viewing their own forms
      loadForms();
    }
  }, [isStaff]);

  useEffect(() => {
    if (selectedPatient || !isStaff) {
      loadForms();
    }
  }, [selectedPatient, loadForms, isStaff]);

  // ABN Functions
  const addServiceItem = () => {
    setAbnData({
      ...abnData,
      items_services: [...abnData.items_services, { description: '', reason: '', estimated_cost: '' }]
    });
  };

  const updateServiceItem = (index, field, value) => {
    const newItems = [...abnData.items_services];
    newItems[index][field] = value;
    setAbnData({ ...abnData, items_services: newItems });
  };

  const removeServiceItem = (index) => {
    if (abnData.items_services.length > 1) {
      const newItems = abnData.items_services.filter((_, i) => i !== index);
      setAbnData({ ...abnData, items_services: newItems });
    }
  };

  const handleCreateAbn = async (e) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      toast.error('Please select a patient first');
      return;
    }
    
    if (!abnData.patient_medicare_id) {
      toast.error('Medicare ID is required');
      return;
    }
    
    if (abnData.items_services.some(item => !item.description || !item.reason)) {
      toast.error('Please fill in all service descriptions and reasons');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/medicare/abn', {
        patient_id: selectedPatient.id,
        patient_name: `${selectedPatient.first_name} ${selectedPatient.last_name}`,
        patient_medicare_id: abnData.patient_medicare_id,
        items_services: abnData.items_services
      });
      
      toast.success('ABN created successfully');
      setShowAbnForm(false);
      setAbnData({
        patient_name: '',
        patient_medicare_id: '',
        items_services: [{ description: '', reason: '', estimated_cost: '' }]
      });
      loadForms();
    } catch (error) {
      toast.error('Failed to create ABN');
    } finally {
      setLoading(false);
    }
  };

  const handleSignAbn = async () => {
    if (!abnChoice) {
      toast.error('Please select an option');
      return;
    }
    if (!abnSignature) {
      toast.error('Please provide your signature');
      return;
    }
    
    setLoading(true);
    try {
      await api.patch(`/medicare/abn/${signingAbn.id}/sign`, {
        patient_choice: abnChoice,
        patient_signature: abnSignature
      });
      
      toast.success('ABN signed successfully');
      setSigningAbn(null);
      setAbnSignature('');
      setAbnChoice('');
      loadForms();
    } catch (error) {
      toast.error('Failed to sign ABN');
    } finally {
      setLoading(false);
    }
  };

  // MSPQ Functions
  const handleCreateMspq = async () => {
    if (!selectedPatient) {
      toast.error('Please select a patient first');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/medicare/mspq', {
        patient_id: selectedPatient.id,
        patient_name: `${selectedPatient.first_name} ${selectedPatient.last_name}`,
        patient_dob: selectedPatient.date_of_birth || '',
        patient_medicare_id: ''
      });
      
      toast.success('MSPQ created');
      setCurrentMspq(response.data.mspq);
      setMspqData({
        ...mspqData,
        patient_name: `${selectedPatient.first_name} ${selectedPatient.last_name}`,
        patient_dob: selectedPatient.date_of_birth || ''
      });
      setShowMspqForm(true);
      loadForms();
    } catch (error) {
      toast.error('Failed to create MSPQ');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMspq = async () => {
    if (!currentMspq) return;
    
    setLoading(true);
    try {
      const response = await api.patch(`/medicare/mspq/${currentMspq.id}`, mspqData);
      
      toast.success('MSPQ updated');
      if (response.data.determination_reason) {
        toast.info(`Determination: ${response.data.determination_reason}`);
      }
      setShowMspqForm(false);
      setCurrentMspq(null);
      loadForms();
    } catch (error) {
      toast.error('Failed to update MSPQ');
    } finally {
      setLoading(false);
    }
  };

  const editMspq = (mspq) => {
    setCurrentMspq(mspq);
    setMspqData({
      patient_name: mspq.patient_name,
      patient_dob: mspq.patient_dob,
      patient_medicare_id: mspq.patient_medicare_id,
      is_employed: mspq.is_employed || false,
      employer_name: mspq.employer_name || '',
      employer_address: mspq.employer_address || '',
      employment_status: mspq.employment_status || '',
      spouse_employed: mspq.spouse_employed || false,
      spouse_employer_name: mspq.spouse_employer_name || '',
      spouse_employer_address: mspq.spouse_employer_address || '',
      has_ghp: mspq.has_ghp || false,
      ghp_name: mspq.ghp_name || '',
      ghp_policy_number: mspq.ghp_policy_number || '',
      ghp_through: mspq.ghp_through || '',
      has_other_insurance: mspq.has_other_insurance || false,
      other_insurance_type: mspq.other_insurance_type || '',
      other_insurance_name: mspq.other_insurance_name || '',
      other_insurance_policy: mspq.other_insurance_policy || '',
      accident_date: mspq.accident_date || '',
      has_esrd: mspq.has_esrd || false,
      esrd_start_date: mspq.esrd_start_date || '',
      has_black_lung: mspq.has_black_lung || false,
      va_eligible: mspq.va_eligible || false,
      service_related: mspq.service_related || false,
      patient_signature: mspq.patient_signature || ''
    });
    setShowMspqForm(true);
  };

  // PDF Functions
  const viewPdf = async (type, id) => {
    try {
      const response = await api.get(`/medicare/${type}/${id}/pdf`);
      setPdfHtml(response.data.html);
    } catch (error) {
      toast.error('Failed to generate form');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'signed':
      case 'completed':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1"><CheckCircle className="w-3 h-3" /> {status}</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">{status}</span>;
    }
  };

  const filteredPatients = patients.filter(p => 
    `${p.first_name} ${p.last_name} ${p.email}`.toLowerCase().includes(patientSearch.toLowerCase())
  );

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="medicare-forms-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="w-7 h-7 text-blue-600" />
          Medicare Forms
        </h1>
        <p className="text-gray-600 mt-1">ABN and MSPQ forms for Medicare patients</p>
      </div>

      {/* Patient Selector (Staff Only) */}
      {isStaff && (
        <div className="bg-white rounded-xl border shadow-sm p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Patient</label>
          <div className="relative">
            <input
              type="text"
              value={patientSearch}
              onChange={(e) => setPatientSearch(e.target.value)}
              placeholder="Search patients..."
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {patientSearch && filteredPatients.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                {filteredPatients.slice(0, 10).map(patient => (
                  <button
                    key={patient.id}
                    onClick={() => {
                      setSelectedPatient(patient);
                      setPatientSearch(`${patient.first_name} ${patient.last_name}`);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                  >
                    <User className="w-4 h-4 text-gray-400" />
                    <span>{patient.first_name} {patient.last_name}</span>
                    <span className="text-gray-400 text-sm">({patient.email})</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedPatient && (
            <div className="mt-2 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
              <div>
                <p className="font-medium text-blue-900">{selectedPatient.first_name} {selectedPatient.last_name}</p>
                <p className="text-sm text-blue-700">{selectedPatient.email}</p>
              </div>
              <button onClick={() => { setSelectedPatient(null); setPatientSearch(''); }} className="text-blue-600 hover:text-blue-800">
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('abn')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'abn' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Warning className="w-4 h-4 inline mr-2" />
          ABN Forms
        </button>
        <button
          onClick={() => setActiveTab('mspq')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'mspq' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Briefcase className="w-4 h-4 inline mr-2" />
          MSPQ Forms
        </button>
      </div>

      {/* ABN Tab */}
      {activeTab === 'abn' && (
        <div className="space-y-6">
          {/* Info Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h3 className="font-semibold text-amber-800 flex items-center gap-2">
              <Warning className="w-5 h-5" />
              Advance Beneficiary Notice (ABN)
            </h3>
            <p className="text-amber-700 text-sm mt-1">
              Required when Medicare may not cover a service. The patient must sign before receiving the service.
            </p>
          </div>

          {/* Create ABN Button (Staff Only) */}
          {isStaff && selectedPatient && (
            <button
              onClick={() => setShowAbnForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              Create New ABN
            </button>
          )}

          {/* ABN Creation Form */}
          {showAbnForm && (
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Create ABN Form</h3>
                <button onClick={() => setShowAbnForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleCreateAbn} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Medicare ID (MBI) *</label>
                  <input
                    type="text"
                    value={abnData.patient_medicare_id}
                    onChange={(e) => setAbnData({...abnData, patient_medicare_id: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="1EG4-TE5-MK72"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Items/Services Medicare May Not Cover *</label>
                  {abnData.items_services.map((item, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateServiceItem(index, 'description', e.target.value)}
                        placeholder="Service description"
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        required
                      />
                      <input
                        type="text"
                        value={item.reason}
                        onChange={(e) => updateServiceItem(index, 'reason', e.target.value)}
                        placeholder="Reason may not be covered"
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        required
                      />
                      <input
                        type="text"
                        value={item.estimated_cost}
                        onChange={(e) => updateServiceItem(index, 'estimated_cost', e.target.value)}
                        placeholder="Est. cost"
                        className="w-24 px-3 py-2 border rounded-lg text-sm"
                      />
                      {abnData.items_services.length > 1 && (
                        <button type="button" onClick={() => removeServiceItem(index)} className="text-red-500 hover:text-red-700">
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addServiceItem} className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Add another item
                  </button>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    {loading ? 'Creating...' : 'Create ABN'}
                  </button>
                  <button type="button" onClick={() => setShowAbnForm(false)} className="px-6 py-2 border rounded-lg hover:bg-gray-50">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ABN List */}
          {abnForms.length > 0 ? (
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-900">ABN Forms</h3>
              </div>
              <div className="divide-y">
                {abnForms.map(abn => (
                  <div key={abn.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{abn.patient_name}</p>
                        <p className="text-sm text-gray-500">Medicare ID: {abn.patient_medicare_id}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          Services: {abn.items_services?.map(s => s.description).join(', ')}
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          {getStatusBadge(abn.status)}
                          <span className="text-xs text-gray-400">
                            Created {new Date(abn.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {abn.status === 'pending' && !isStaff && (
                          <button
                            onClick={() => setSigningAbn(abn)}
                            className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                          >
                            Sign ABN
                          </button>
                        )}
                        <button
                          onClick={() => viewPdf('abn', abn.id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View/Print"
                        >
                          <Printer className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            !showAbnForm && (selectedPatient || !isStaff) && (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <h3 className="text-lg font-medium text-gray-900">No ABN Forms</h3>
                <p className="text-gray-500">No Advance Beneficiary Notices on file</p>
              </div>
            )
          )}
        </div>
      )}

      {/* MSPQ Tab */}
      {activeTab === 'mspq' && (
        <div className="space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-semibold text-blue-800 flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Medicare Secondary Payer Questionnaire (MSPQ)
            </h3>
            <p className="text-blue-700 text-sm mt-1">
              Determines if Medicare is the primary or secondary payer based on employment and other insurance coverage.
            </p>
          </div>

          {/* Create MSPQ Button (Staff Only) */}
          {isStaff && selectedPatient && (
            <button
              onClick={handleCreateMspq}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              Create New MSPQ
            </button>
          )}

          {/* MSPQ Form */}
          {showMspqForm && (
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Medicare Secondary Payer Questionnaire</h3>
                <button onClick={() => { setShowMspqForm(false); setCurrentMspq(null); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Patient Info */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name</label>
                    <input type="text" value={mspqData.patient_name} readOnly className="w-full px-3 py-2 border rounded-lg bg-gray-50" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={mspqData.patient_dob}
                      onChange={(e) => setMspqData({...mspqData, patient_dob: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medicare ID (MBI)</label>
                    <input
                      type="text"
                      value={mspqData.patient_medicare_id}
                      onChange={(e) => setMspqData({...mspqData, patient_medicare_id: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="1EG4-TE5-MK72"
                    />
                  </div>
                </div>

                {/* Employment Section */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Employment Status
                  </h4>
                  
                  <div className="space-y-4">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={mspqData.is_employed}
                        onChange={(e) => setMspqData({...mspqData, is_employed: e.target.checked})}
                        className="rounded text-blue-600"
                      />
                      <span>Patient is currently employed</span>
                    </label>
                    
                    {mspqData.is_employed && (
                      <div className="ml-7 grid md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={mspqData.employer_name}
                          onChange={(e) => setMspqData({...mspqData, employer_name: e.target.value})}
                          placeholder="Employer Name"
                          className="px-3 py-2 border rounded-lg"
                        />
                        <input
                          type="text"
                          value={mspqData.employer_address}
                          onChange={(e) => setMspqData({...mspqData, employer_address: e.target.value})}
                          placeholder="Employer Address"
                          className="px-3 py-2 border rounded-lg"
                        />
                      </div>
                    )}
                    
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={mspqData.spouse_employed}
                        onChange={(e) => setMspqData({...mspqData, spouse_employed: e.target.checked})}
                        className="rounded text-blue-600"
                      />
                      <span>Spouse is currently employed</span>
                    </label>
                    
                    {mspqData.spouse_employed && (
                      <div className="ml-7 grid md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={mspqData.spouse_employer_name}
                          onChange={(e) => setMspqData({...mspqData, spouse_employer_name: e.target.value})}
                          placeholder="Spouse's Employer Name"
                          className="px-3 py-2 border rounded-lg"
                        />
                        <input
                          type="text"
                          value={mspqData.spouse_employer_address}
                          onChange={(e) => setMspqData({...mspqData, spouse_employer_address: e.target.value})}
                          placeholder="Spouse's Employer Address"
                          className="px-3 py-2 border rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* GHP Section */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                    <FirstAidKit className="w-4 h-4" /> Group Health Plan (GHP)
                  </h4>
                  
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={mspqData.has_ghp}
                      onChange={(e) => setMspqData({...mspqData, has_ghp: e.target.checked})}
                      className="rounded text-blue-600"
                    />
                    <span>Covered by Group Health Plan through employer</span>
                  </label>
                  
                  {mspqData.has_ghp && (
                    <div className="ml-7 mt-4 grid md:grid-cols-3 gap-4">
                      <input
                        type="text"
                        value={mspqData.ghp_name}
                        onChange={(e) => setMspqData({...mspqData, ghp_name: e.target.value})}
                        placeholder="Insurance Name"
                        className="px-3 py-2 border rounded-lg"
                      />
                      <input
                        type="text"
                        value={mspqData.ghp_policy_number}
                        onChange={(e) => setMspqData({...mspqData, ghp_policy_number: e.target.value})}
                        placeholder="Policy Number"
                        className="px-3 py-2 border rounded-lg"
                      />
                      <select
                        value={mspqData.ghp_through}
                        onChange={(e) => setMspqData({...mspqData, ghp_through: e.target.value})}
                        className="px-3 py-2 border rounded-lg"
                      >
                        <option value="">Coverage through...</option>
                        <option value="self">Self</option>
                        <option value="spouse">Spouse</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Other Insurance Section */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-800 mb-3">Other Insurance Coverage</h4>
                  
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={mspqData.has_other_insurance}
                      onChange={(e) => setMspqData({...mspqData, has_other_insurance: e.target.checked})}
                      className="rounded text-blue-600"
                    />
                    <span>Has other insurance coverage</span>
                  </label>
                  
                  {mspqData.has_other_insurance && (
                    <div className="ml-7 mt-4 space-y-4">
                      <select
                        value={mspqData.other_insurance_type}
                        onChange={(e) => setMspqData({...mspqData, other_insurance_type: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">Select type...</option>
                        <option value="workers_comp">Workers Compensation</option>
                        <option value="auto_accident">Auto Accident / No-Fault</option>
                        <option value="liability">Liability Insurance</option>
                        <option value="va">Veterans Affairs</option>
                        <option value="tricare">TRICARE</option>
                      </select>
                      
                      {mspqData.other_insurance_type && (
                        <div className="grid md:grid-cols-2 gap-4">
                          <input
                            type="text"
                            value={mspqData.other_insurance_name}
                            onChange={(e) => setMspqData({...mspqData, other_insurance_name: e.target.value})}
                            placeholder="Insurance Company Name"
                            className="px-3 py-2 border rounded-lg"
                          />
                          <input
                            type="text"
                            value={mspqData.other_insurance_policy}
                            onChange={(e) => setMspqData({...mspqData, other_insurance_policy: e.target.value})}
                            placeholder="Policy/Claim Number"
                            className="px-3 py-2 border rounded-lg"
                          />
                          {['workers_comp', 'auto_accident'].includes(mspqData.other_insurance_type) && (
                            <input
                              type="date"
                              value={mspqData.accident_date}
                              onChange={(e) => setMspqData({...mspqData, accident_date: e.target.value})}
                              className="px-3 py-2 border rounded-lg"
                              placeholder="Accident Date"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Special Conditions */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-800 mb-3">Special Conditions</h4>
                  
                  <div className="space-y-3">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={mspqData.has_esrd}
                        onChange={(e) => setMspqData({...mspqData, has_esrd: e.target.checked})}
                        className="rounded text-blue-600"
                      />
                      <span>End-Stage Renal Disease (ESRD)</span>
                    </label>
                    
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={mspqData.has_black_lung}
                        onChange={(e) => setMspqData({...mspqData, has_black_lung: e.target.checked})}
                        className="rounded text-blue-600"
                      />
                      <span>Black Lung Disease</span>
                    </label>
                    
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={mspqData.va_eligible}
                        onChange={(e) => setMspqData({...mspqData, va_eligible: e.target.checked})}
                        className="rounded text-blue-600"
                      />
                      <span>VA Benefits Eligible</span>
                    </label>
                  </div>
                </div>

                {/* Signature */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-800 mb-3">Patient Signature</h4>
                  <input
                    type="text"
                    value={mspqData.patient_signature}
                    onChange={(e) => setMspqData({...mspqData, patient_signature: e.target.value})}
                    placeholder="Type full legal name to sign"
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleUpdateMspq}
                    disabled={loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save & Submit'}
                  </button>
                  <button
                    onClick={() => { setShowMspqForm(false); setCurrentMspq(null); }}
                    className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* MSPQ List */}
          {mspqForms.length > 0 ? (
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-900">MSPQ Forms</h3>
              </div>
              <div className="divide-y">
                {mspqForms.map(mspq => (
                  <div key={mspq.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{mspq.patient_name}</p>
                        <p className="text-sm text-gray-500">Medicare ID: {mspq.patient_medicare_id || 'Not provided'}</p>
                        {mspq.determination_reason && (
                          <p className="text-sm mt-1">
                            <span className={`font-medium ${mspq.medicare_primary ? 'text-green-600' : 'text-amber-600'}`}>
                              {mspq.medicare_primary ? '✓ Medicare Primary' : '⚠ Medicare Secondary'}
                            </span>
                            <span className="text-gray-500 ml-2">- {mspq.determination_reason}</span>
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-2">
                          {getStatusBadge(mspq.status)}
                          <span className="text-xs text-gray-400">
                            Created {new Date(mspq.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {mspq.status === 'pending' && (
                          <button
                            onClick={() => editMspq(mspq)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                            title="Edit"
                          >
                            <PencilSimple className="w-5 h-5" />
                          </button>
                        )}
                        <button
                          onClick={() => viewPdf('mspq', mspq.id)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View/Print"
                        >
                          <Printer className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            !showMspqForm && (selectedPatient || !isStaff) && (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <h3 className="text-lg font-medium text-gray-900">No MSPQ Forms</h3>
                <p className="text-gray-500">No Medicare Secondary Payer Questionnaires on file</p>
              </div>
            )
          )}
        </div>
      )}

      {/* ABN Signing Modal */}
      {signingAbn && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl p-6">
            <h3 className="text-lg font-semibold mb-4">Sign Advance Beneficiary Notice</h3>
            
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-medium text-amber-800">Services that may not be covered:</p>
                <ul className="mt-2 space-y-1">
                  {signingAbn.items_services?.map((item) => (
                    <li key={`abn-item-${item.description}-${item.reason}`} className="text-amber-700">• {item.description} - {item.reason}</li>
                  ))}
                </ul>
              </div>
              
              <div className="space-y-3">
                <p className="font-medium">Choose an option:</p>
                
                <label className={`block p-3 border rounded-lg cursor-pointer ${abnChoice === 'option1' ? 'border-blue-500 bg-blue-50' : ''}`}>
                  <input
                    type="radio"
                    name="abnChoice"
                    value="option1"
                    checked={abnChoice === 'option1'}
                    onChange={(e) => setAbnChoice(e.target.value)}
                    className="mr-2"
                  />
                  <strong>Option 1:</strong> I want the service. Bill Medicare. I may appeal if denied.
                </label>
                
                <label className={`block p-3 border rounded-lg cursor-pointer ${abnChoice === 'option2' ? 'border-blue-500 bg-blue-50' : ''}`}>
                  <input
                    type="radio"
                    name="abnChoice"
                    value="option2"
                    checked={abnChoice === 'option2'}
                    onChange={(e) => setAbnChoice(e.target.value)}
                    className="mr-2"
                  />
                  <strong>Option 2:</strong> I want the service. Do NOT bill Medicare. I will pay.
                </label>
                
                <label className={`block p-3 border rounded-lg cursor-pointer ${abnChoice === 'option3' ? 'border-blue-500 bg-blue-50' : ''}`}>
                  <input
                    type="radio"
                    name="abnChoice"
                    value="option3"
                    checked={abnChoice === 'option3'}
                    onChange={(e) => setAbnChoice(e.target.value)}
                    className="mr-2"
                  />
                  <strong>Option 3:</strong> I do NOT want the service.
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Signature (type your full legal name)
                </label>
                <input
                  type="text"
                  value={abnSignature}
                  onChange={(e) => setAbnSignature(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="John Doe"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSignAbn}
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Signing...' : 'Sign ABN'}
                </button>
                <button
                  onClick={() => { setSigningAbn(null); setAbnChoice(''); setAbnSignature(''); }}
                  className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {pdfHtml && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Form Preview</h3>
              <button onClick={() => setPdfHtml(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe srcDoc={pdfHtml} className="w-full h-full min-h-[600px]" title="Form Preview" />
            </div>
          </div>
        </div>
      )}

      {/* No Patient Selected Message (Staff) */}
      {isStaff && !selectedPatient && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <User className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900">Select a Patient</h3>
          <p className="text-gray-500">Search and select a patient above to view or create Medicare forms</p>
        </div>
      )}
    </div>
  );
}
